import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';

import {
  FiatWalletAccountState,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { isCryptocurrency } from '@sorare/core/src/lib/cryptocurrency';
import { isTypeFn, withFragments } from '@sorare/core/src/lib/gql';

import type { useRewardsVerification_so5Reward } from './__generated__/useRewardsVerification.graphql';

/*
Rewards need verification before claim:
- Box rewards            -> Phone number validation
- Card rewards           -> Phone number validation
- Card rewards (Common)  -> No verification needed
- Cash rewards (FIAT)    -> KYC level 2 (VALIDATED_OWNER)
- Cash rewards (ETH)     -> No verification needed

Deliverable items :
- Claim (claimRewards mutation) -> create an deliverableItemOrder
- Order (claimDeliverableItemOrder mutation) -> persist order delivery data
*/

export type RewardByType<T> = {
  box: T[];
  card: T[];
  cash: T[];
  coin: T[];
  shards: T[];
  conversionCredit: T[];
  deliverableItem: T[];
  inGameCurrencies: T[];
  shopItem: T[];
  total: number;
};

const getTotal = <T extends object>(obj: RewardByType<T>) => {
  return Object.values(obj).reduce<number>(
    (sum, value) => (Array.isArray(value) ? value.length : 0) + sum,
    0
  );
};

export const useRewardsVerification = withFragments(
  <T extends useRewardsVerification_so5Reward>(so5Rewards: T[]) => {
    const { currentUser } = useCurrentUserContext();
    const { fiatWalletAccountable } = useCurrentUserContext();

    const kycCompleted =
      fiatWalletAccountable?.state === FiatWalletAccountState.VALIDATED_OWNER;
    const { phoneNumberVerified } = currentUser || {};

    return useMemo(() => {
      const rewardsByType: RewardByType<T> = {
        cash: [],
        box: [],
        card: [],
        coin: [],
        shards: [],
        conversionCredit: [],
        deliverableItem: [],
        inGameCurrencies: [],
        shopItem: [],
        total: 0,
      };
      const unclaimedRewardsByType: RewardByType<T> = {
        cash: [],
        box: [],
        card: [],
        coin: [],
        shards: [],
        conversionCredit: [],
        deliverableItem: [],
        inGameCurrencies: [],
        shopItem: [],
        total: 0,
      };
      const claimableRewardsByType: RewardByType<T> = {
        cash: [],
        box: [],
        card: [],
        coin: [],
        shards: [],
        conversionCredit: [],
        deliverableItem: [],
        inGameCurrencies: [],
        shopItem: [],
        total: 0,
      };

      so5Rewards.forEach(reward => {
        if (reward.rewards.some(isTypeFn('ProbabilisticBundleReward'))) {
          rewardsByType.box.push(reward);
          if (reward.aasmState !== 'claimed') {
            unclaimedRewardsByType.box.push(reward);
            if (phoneNumberVerified) {
              claimableRewardsByType.box.push(reward);
            }
          }
        }
        const monetaryReward = reward.rewards.find(isTypeFn('MonetaryReward'));
        if (monetaryReward) {
          const { referenceCurrency } = monetaryReward.amount;
          rewardsByType.cash.push(reward);
          if (reward.aasmState !== 'claimed') {
            unclaimedRewardsByType.cash.push(reward);
            if (kycCompleted || isCryptocurrency(referenceCurrency)) {
              claimableRewardsByType.cash.push(reward);
            }
          }
        }
        const cardRewards = reward.rewards.filter(isTypeFn('AnyCardReward'));
        if (cardRewards.length > 0) {
          rewardsByType.card.push(reward);
          if (reward.aasmState !== 'claimed') {
            unclaimedRewardsByType.card.push(reward);
            if (
              phoneNumberVerified ||
              cardRewards.every(({ card }) => card.rarity === Rarity.common)
            ) {
              claimableRewardsByType.card.push(reward);
            }
          }
        }
        if (reward.rewards.some(isTypeFn('CoinReward'))) {
          rewardsByType.coin.push(reward);
          if (reward.aasmState !== 'claimed') {
            unclaimedRewardsByType.coin.push(reward);
            claimableRewardsByType.coin.push(reward);
          }
        }
        if (reward.rewards.some(isTypeFn('CardShardsReward'))) {
          rewardsByType.shards.push(reward);
          if (reward.aasmState !== 'claimed') {
            unclaimedRewardsByType.shards.push(reward);
            claimableRewardsByType.shards.push(reward);
          }
        }
        if (reward.rewards.some(isTypeFn('AnyConversionCreditReward'))) {
          rewardsByType.conversionCredit.push(reward);
          if (reward.aasmState !== 'claimed') {
            unclaimedRewardsByType.conversionCredit.push(reward);
            claimableRewardsByType.conversionCredit.push(reward);
          }
        }
        if (reward.rewards.some(isTypeFn('InGameCurrencyReward'))) {
          rewardsByType.inGameCurrencies.push(reward);
          if (reward.aasmState !== 'claimed') {
            unclaimedRewardsByType.inGameCurrencies.push(reward);
            claimableRewardsByType.inGameCurrencies.push(reward);
          }
        }
        if (reward.rewards.some(isTypeFn('DeliverableItemReward'))) {
          rewardsByType.deliverableItem.push(reward);
          if (reward.aasmState !== 'claimed') {
            unclaimedRewardsByType.deliverableItem.push(reward);
            claimableRewardsByType.deliverableItem.push(reward);
          }
        }
        if (reward.rewards.some(isTypeFn('ShopItemReward'))) {
          rewardsByType.shopItem.push(reward);
          if (reward.aasmState !== 'claimed') {
            unclaimedRewardsByType.shopItem.push(reward);
            claimableRewardsByType.shopItem.push(reward);
          }
        }
      });

      rewardsByType.total = getTotal(rewardsByType);
      unclaimedRewardsByType.total = getTotal(unclaimedRewardsByType);
      claimableRewardsByType.total = getTotal(claimableRewardsByType);

      const needVerifiedPhoneNumber =
        rewardsByType.box.length > 0 ||
        !rewardsByType.card.every(c =>
          c.rewards
            .filter(isTypeFn('AnyCardReward'))
            .every(({ card }) => card.rarity === Rarity.common)
        );
      const needVerifiedAccount = rewardsByType.cash
        .flatMap(({ rewards }) => rewards.filter(isTypeFn('MonetaryReward')))
        .some(({ amount }) => !isCryptocurrency(amount?.referenceCurrency));

      return {
        rewardsByType,
        unclaimedRewardsByType,
        claimableRewardsByType,
        verificationNeeded: {
          kyc: needVerifiedAccount && !kycCompleted,
          phoneNumber: needVerifiedPhoneNumber && !phoneNumberVerified,
        },
      };
    }, [kycCompleted, phoneNumberVerified, so5Rewards]);
  },
  {
    so5Reward: gql`
      fragment useRewardsVerification_so5Reward on So5Reward {
        slug
        aasmState
        rewards {
          id
          ... on AnyCardReward {
            id
            card {
              slug
              rarity: rarityTyped
            }
          }
          ... on MonetaryReward {
            id
            amount {
              referenceCurrency
            }
          }
        }
      }
    ` as TypedDocumentNode<useRewardsVerification_so5Reward>,
  }
);
